import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ACCOUNT_CREATED,
  GRANTABLE_TENANT_ROLES,
  CHANGE_NOT_IN_EFFECT,
  LAST_ADMINISTRATOR,
  MAIL_SEND_FAILED,
  grantTenantRole,
  listTenantRoleHolders,
  revokeTenantRole,
  type Tenant,
  type TenantRoleGrant,
  type TenantRoleHolder,
} from "./tenantsApi";
import {
  filterHolders,
  grantedBannerKey,
  groupHolders,
  holderLabel,
  revokeConfirmKey,
  revokedBannerKey,
  showsHomeOrganisation,
} from "./tenantRoleHolders";
import { ApiError, SessionExpiredError } from "@web/lib/api";
import { ROLE_TONE, hasVocabularyEntry } from "@web/admin/roleVocabulary";
import { Modal } from "@web/ui";

// ROOLIT TÄSSÄ ASIAKKAASSA — ADR-0018 §7's grant surface, as a section of the tenant's own page
// (`pages/TenantPage.tsx`). The codes it offers are `GRANTABLE_TENANT_ROLES`, and NOTHING here names one:
// the heading, the boxes and the hint are all written against the set, because §3.3 has widened three times
// (CONTENT_MANAGER, 27.10.2026; HRM_HENKILOSTOASIANTUNTIJA, ADR-0021 §3.4, 30.10.2026; ADMIN, ADR-0026 §3,
// 3.11.2026) and each time the whole diff on this side was the array plus two locale keys per code —
// `tenants.roles.code.<CODE>` for the tick-list label and `users.role.<CODE>` for the holder chip
// (`platform-i18n.test.mjs` asserts both, in both locales, off the array itself).
//
// IT STOPPED BEING "INCEDON ROOLIT" WITH THAT THIRD WIDENING, and the heading and the intro moved with it.
// Every earlier code names a job an Incedo person does inside a customer, so the old title described the
// list exactly; `ADMIN` does not. The customer's OWN pääkäyttäjä holds it — on the client-role carrier, the
// one the first-administrator invite writes — so they are a row on this list, with a removal on their chip,
// in a section that until this task told the operator it was showing them Incedo's people. The strings say
// what the list shows: the roles granted from here, held by whoever holds them.
//
// WHAT THE OPERATOR DOES HERE, AND WHAT THEY DELIBERATELY DO NOT HAVE TO KNOW. They type an address and
// TICK the role codes that address should hold. The server then chooses between two entirely different
// Keycloak writes — a group membership for an account bound to ANOTHER tenant, the client role for one
// bound to THIS one — from a fact already in the realm. That choice is not on this screen and must not
// become a question: an operator who had to know which carrier a person needs would be reading somebody's
// `tenant_id` out of the Keycloak console to fill in a form, which is the manual step this whole ADR
// removes.
//
// WHY THE ROLE IS A MULTI-CHOICE AND NOT A PICKER. One person really can hold several — a vastuukonsultti
// who is also the HR expert for the same customer is an ordinary arrangement (ADR-0018 §3.3), and the
// holder list has always been keyed by the PAIR rather than the person for exactly that reason. A single
// picker made that ordinary case two submissions of the same address, which is two chances to mistype it.
//
// The route takes ONE role per call, so a multi-role tick is several calls — IN TURN, never at once. The first
// call is the one that may create the realm account and send Keycloak's activation mail; a second call
// racing it would be two creations of one address. So they are awaited one after the other, and the
// consequence is a state the single-role form never had: HALF the grant can land. Nothing is rolled back
// when it does — a role that is in effect is somebody's real access, and undoing it to make the form's
// outcome tidy would take an authority away that no operator asked to remove. The message names which
// half is in effect and which is not, and the reason belongs to the half that failed.
//
// WHICH MEANS A FAILED CALL IS NOT THE SAME AS AN UNGRANTED ROLE, and this surface is one of the few that
// has to tell them apart. The route DECLARES one 500, `AuditTrailIncomplete`: one of the two audit chains
// is missing its row. What that means for the role depends on the CARRIER, and since ADR-0021 §8.1 the two
// answers are opposite ones, told apart by the problem's `error` code:
//
//   * `change_in_effect` — the ending this screen has always had. A client role is a Keycloak write nothing
//     can take back, so the role IS in effect and only the customer's row is missing: a retry is the one
//     act the response rules out. A console that counted every thrown response as "not granted" would say
//     the opposite of the response it is quoting, and hand the operator back a ticked box inviting it.
//   * `change_not_in_effect` — a cross-tenant membership is a ROW, written inert and activated only once
//     both audit rows are in, so a chain that could not be written means NOBODY'S ACCESS CHANGED. Here the
//     retry is the remedy, the box comes back ticked, and a console that read this as "in effect" would
//     tell the operator somebody can reach a customer when they cannot. Its two causes leave DIFFERENT
//     things behind — the customer's chain leaves the row inert, the operator's chain is inside the
//     transaction that writes the row and leaves none — so the message says only what holds of both.
//
// AND A 500 IS NOT ALWAYS THAT ONE. Outside Development the pipeline turns any unhandled exception into a
// generic ProblemDetails 500 (`Program.cs`), and this route has two places that reach it: the database
// query that finds the customer, which runs BEFORE the realm is touched, and the operator-side audit append
// that runs AFTER the realm write has landed. One of those 500s means nothing was written and the other
// means the role is in effect — and the response does not say which. So the declared ending is recognised
// by the RESPONSE and not by the status, and a 500 that is not it leaves that code's outcome UNKNOWN, which
// the form says in those words rather than picking the half it prefers.
//
// So each ticked code ends in one of FIVE states — granted, APPLIED (in effect, missing the customer's
// row), NOT IN EFFECT (nobody's access changed, one of the two chains missing), UNDETERMINED (the server
// broke without saying), or not granted. Applied is the one that is never offered again, because its own
// response forbids the retry; the other three are offered again, because the writes are idempotent
// (`TenantRoleGrantService`) and the holder list read below is what settles what they left behind.
//
// THE CARRIER IS NO LONGER ON THE SCREEN AT ALL, which reverses what this file said until this task. It
// used to be on every holder row as "the honest answer to why this consultant can be in five customers at
// once" — and the owner's report (30.10.2026) is the answer to that: on a list of people and their roles,
// "tenanttijäsenyys (tunnus toisessa tenantissa)" is a sentence about Keycloak's internals in the place an
// operator is trying to read a name. It never was something they could act on either, because the removal
// reads the carrier out of the realm again (§7). It stays on each chip as `data-carrier` — in the DOM, out
// of the accessibility tree and out of the reader's way — rather than as a `title`, which would REPLACE
// the chip's own accessible name with it and so hand the carrier to exactly the reader least able to skip
// it. Which carrier a role sits on is still asserted where it is a fact rather than a label: at the route,
// in `platform-tenant-roles.realstack.spec.ts`.
//
// IT STILL DECIDES ONE THING THE OPERATOR READS, and that is not the label coming back: the BOUND a
// completed grant or removal announces. A membership is a row read on every request with no cache
// (ADR-0021 §4.3), so it is live at the grantee's next request and a removal has already taken effect; a
// client role is a claim on their token, so both ends of it wait for the next sign-in. One sentence for
// both said "next sign-in", which sent a consultant out of a session for nothing and — the dangerous half
// — told a support desk that access the product had already cut would end later. The two keys are chosen
// in `tenantRoleHolders.ts`, where the choice can be tested without a DOM, and the carrier they are chosen
// from is the WRITE's own answer in both directions — each answered grant's `holder.carrier` and the
// removal's — never the list row on screen, which was read before the operator clicked.
//
// ONE ROW PER PERSON, not per (person, role) pair, which is the other half of that report. See
// `tenantRoleHolders.ts` for the grouping and why it is a module of its own.
//
// AND A THIRD LINE WHEN THE PERSON'S ACCOUNT BELONGS SOMEWHERE ELSE — their HOME ORGANISATION, which
// ADR-0021 §8.2.1 put on the wire for this row (`homeTenantId` / `homeTenantName`). ac90583a asked for it
// and could not have it: nothing on `TenantRoleHolderDto` named the tenant an account was bound to, so it
// was a backend capability rather than a rendering change. It is the readable half of what the carrier used
// to say: the carrier distinguished "bound elsewhere" from "the customer's own person" and never said
// WHERE, which is the thing an operator actually wants to know about a name they do not recognise.
//
// WHY THE HOLDER READ IS BEHIND A CLICK. `GET …/roles` writes a `TENANT_ACCESSED` audit row
// (`{"viewed":"tenant_role_holders"}`, FR-Q.35) and discloses the customer's people — the same reasoning
// as `TenantPrincipalSection`, and the same arrangement: nothing is read until the operator asks, and the
// closed state SAYS so instead of leaving it a silent property. The FR-Q.35 audit guard in
// `e2e/platform-tenant.realstack.spec.ts` watches that the page load issues no request to this route.
//
// AN UNCERTAIN LIST IS NOT AN EMPTY ONE. A 502 here means the identity provider did not answer, and the
// section says exactly that rather than rendering "ei haltijoita" — ADR-0016 §4.7 point 4. "Nobody can
// read this customer's raw observations" is a claim about access, and a surface that made it because a
// read timed out would be the quietest possible way to get it wrong.
//
// EVERY REFUSAL GETS ITS OWN MESSAGE, because they ask different things of the operator, and more than one
// of them shares a status with another — so the problem's `error` code and not the status is what picks the
// sentence. The grant's 422 says the CODE is wrong, and the server's own detail names the rule that refused
// it (PLATFORM_ADMIN is ADR-0018 §8's support grant, the two MJ codes are granted inside the product); its
// sibling was §9's twenty-tenant ceiling, and ADR-0021 §7 removed both. The 500 is the one case where the
// change IS in effect, and its message says so instead of inviting a retry.
//
// THE REMOVAL HAS TWO REFUSALS OF ITS OWN, and only ONE of them is a refusal at all:
//
//   * 409 `last_administrator` — ADR-0026 §4's floor. This would have been the customer's last `ADMIN`, so
//     nothing was removed, and the one act that clears the way is on this same form: grant `ADMIN` to
//     somebody else first. It is the only refusal here whose remedy is another use of this screen, which is
//     why collapsing it into the surface's other 409 (an account whose `tenant_id` names no tenant, fixed in
//     the Keycloak console) would send the operator to the wrong place entirely.
//   * 502 — which covers THREE endings the backend answers identically, with no code to tell them apart,
//     and they do NOT agree about what happened. Two are refusals BEFORE any write: a read that would not
//     say how the account holds the role, and §4c's INDETERMINATE count, which fails CLOSED because an
//     unreadable realm must not render as "somebody else still holds it" any more than as "nobody does".
//     The third is the realm write ITSELF not completing — `TenantRoleGrantService.RevokeAsync` reaches it
//     when `RevokeClientRoleAsync` answers false, and `KeycloakAdminClient.WriteRoleMappingAsync` answers
//     false for a DELETE it had already sent whose response was lost or timed out, which is why the
//     server's own detail for that ending says the role "may still be held".
//
//     SO THE MESSAGE MAY NOT PROMISE EITHER OUTCOME. It says the one thing true of all three — the outcome
//     is not known, the role may be in effect or may be gone — and it invites the retry, which is safe
//     because the realm write is idempotent both ways (`WriteRoleMappingAsync` writes nothing when the realm
//     already says what was asked for). The two things it must not say are the two that would be read as
//     settled access: the 500's "the removal IS in effect, do not try again", which forbids the act that
//     would actually finish the job, and the 409s' "nothing was removed", which only a KNOWN pre-write
//     refusal may claim. A console that promised the role was still held after a lost DELETE would be
//     telling a support desk that somebody still has access the product may already have taken away.

/**
 * What ONE role's removal takes away: the pair the route names, plus the person as the list named them.
 *
 * The person is carried rather than looked up again because the list is the richer name — for a holder of
 * this tenant's own it is the display name the directory decrypted, which a removal addressed by subject
 * has no reason to go and read a second time. It is also the one thing the RESPONSE cannot supply: a
 * removal's `holder` carries no name and no address (`RevokeMembershipAsync` and its client-role sibling
 * build it from the subject and the role alone).
 *
 * THE CARRIER HERE IS FOR THE CONFIRMATION ONLY, never for the banner. The bound the removal's banner may
 * claim is decided by the carrier the removal actually took ({@link revokedBannerKey}), and the response
 * says which: `TenantRoleGrantDto.holder.carrier`, filled from the fact the service itself read — the
 * `tenant_memberships` row if there is one, the realm otherwise (§7). The clicked row's value is a copy of
 * a list read before the click, so the two can differ exactly when it matters: a role re-granted on the
 * other carrier in between would have the banner name the bound of a carrier this removal did not touch.
 */
interface PendingRevoke {
  readonly subject: string;
  readonly role: string;
  readonly person: string;
  /**
   * The CARRIER of the clicked chip (`HeldRole.carrier`), which decides what the confirmation may promise
   * about when the access goes — see `revokeConfirmKey`. Carried from the row because the confirmation is
   * shown BEFORE the removal is sent, so there is no response yet and the list's own row is the only place
   * this is known. The banner afterwards does not read it (see above).
   */
  readonly carrier: string;
}

/** Where this section is. The holder list, once read, is held beside it. */
type Phase =
  /** Nothing has been read: the section offers the action and names the audit row opening it writes. */
  | { kind: "closed" }
  | { kind: "loading" }
  /** The read failed. `uncertain` is the 502 — the realm did not answer, so the list is UNKNOWN. */
  | { kind: "loadError"; uncertain: boolean }
  | { kind: "open" }
  /** A grant is in flight. */
  | { kind: "granting" }
  /** The removal's confirmation is up, naming the ROLE and the person it would take it from. */
  | { kind: "confirmRevoke"; pending: PendingRevoke }
  /** The removal is in flight — the dialog may not be taken away under it. */
  | { kind: "revoking"; pending: PendingRevoke };

/**
 * The CHIP's words: the role vocabulary of the product's own user directory (`roleVocabulary.ts`,
 * `users.role.*`), so a code reads the same on this console as it does in the customer's admin user list.
 *
 * Not `tenants.roles.code.*`, which is this form's tick-list label and carries the raw code in front of
 * the name ("MJ_VASTUUKONSULTTI — vastuukonsultti") — right for a box the operator is choosing between,
 * far too long for a chip beside a name.
 *
 * A code the vocabulary does not name falls back to the code itself. The holder list only ever carries
 * §3.3's codes (`ListHoldersAsync` filters to `GrantableRoles`), so this is a guard and not a case: what
 * it rules out is `t()` rendering the missing key — "users.role.FOO" — as if it were a role name.
 */
function roleName(t: (key: string) => string, code: string): string {
  return hasVocabularyEntry(code) ? t(`users.role.${code}`) : code;
}

/**
 * The same role where the sentence must be UNAMBIGUOUS about which code is meant: the removal's
 * confirmation and the banner it writes. Both the code and its name, because the confirmation is where an
 * authority is taken away from a named person — the name alone reads well and the code alone is what the
 * operator ticked, and a decision point is the wrong place to make them choose which they trust.
 */
function roleFull(t: (key: string) => string, code: string): string {
  return hasVocabularyEntry(code) ? `${code} — ${t(`users.role.${code}`)}` : code;
}

/**
 * The chip's tone class, from the SAME hue table the user directory's role pill uses (`ROLE_TONE`), so a
 * code is the same colour on this console as in the customer's own list.
 *
 * `RolePill` itself is not used, because a chip here carries its removal: the component renders a bare
 * `<span>` with no room for a child control, and wrapping a button in it would put an interactive element
 * inside the element whose text labels it. So the chip borrows the look (`.role-pill` and these tones) and
 * owns its own layout. `info` is `RolePill`'s default and has no class of its own.
 */
function chipTone(code: string): string {
  const tone = ROLE_TONE[code];
  return tone === undefined || tone === "info" ? "" : ` role-pill--${tone}`;
}

export function TenantRolesSection({
  tenant,
  headingId,
  onDone,
  onHolders,
}: {
  tenant: Tenant;
  /** The id of this section's own <h2> — the page owns the heading, this owns everything under it. */
  headingId: string;
  /** A completed grant or removal, for the page's banner. */
  onDone: (message: string) => void;
  /**
   * How many holders this tenant has, once the section has read it. `null` means the read did not
   * establish it — the nav is NOT told a number it does not know, and it is never a reason to perform
   * the audited read.
   */
  onHolders: (count: number | null) => void;
}) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>({ kind: "closed" });
  const [holders, setHolders] = useState<TenantRoleHolder[]>([]);
  const [email, setEmail] = useState("");
  /**
   * The role codes that are ticked. EMPTY to start, which is the one real cost of making this a
   * multi-choice and is paid deliberately: a pre-ticked box is the quiet way to grant an authority nobody
   * asked for — the operator who wants only HR ticks HR, submits, and has named a vastuukonsultti and a
   * content manager too. One click for the common single grant is the cheaper mistake, and the refusal
   * below is what stops an empty tick from reaching the route.
   */
  const [roles, setRoles] = useState<readonly string[]>([]);
  const [error, setError] = useState<string | null>(null);
  /**
   * The search box's text. It narrows the list on screen and NOTHING else: no read is re-issued for it
   * (the read is audited — see below — so a request per keystroke would be a disclosure chain made of
   * typing), and it is not a filter the server knows about.
   */
  const [query, setQuery] = useState("");

  const openRef = useRef<HTMLButtonElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  /** Aborts the holder read if the operator closes the section before it lands. */
  const readRef = useRef<AbortController | null>(null);
  /**
   * The section has just OPENED and the keyboard still has to follow it. A flag read by an effect rather
   * than a focus call in the response handler: the field focus goes to is rendered by the same state
   * update, so the only point at which it certainly exists is after React has committed. The flag is also
   * what keeps this from firing again when a cancelled confirmation returns to `open` — the Modal hands
   * focus back to the control that opened it, and a second claim here would take it away.
   */
  const focusOnOpen = useRef(false);

  useEffect(() => () => readRef.current?.abort(), []);

  useEffect(() => {
    if (phase.kind !== "open" || !focusOnOpen.current) return;
    focusOnOpen.current = false;
    emailRef.current?.focus();
  }, [phase]);

  /**
   * THE audited read. It runs from a click and from a completed write, and from nowhere else.
   *
   * `keepError` is for the one write whose message has to OUTLIVE the refresh: a half-landed multi-role
   * grant both wrote a role (so the list is now wrong on screen and must be re-read) and failed (so the
   * operator has to be told which part). Everywhere else the error is the previous attempt's and a fresh
   * look at the list is exactly the point at which it stops being true.
   */
  const read = useCallback(
    ({ focus, keepError = false }: { focus: boolean; keepError?: boolean }) => {
      if (!keepError) setError(null);
      setPhase({ kind: "loading" });
      readRef.current?.abort();
      const controller = new AbortController();
      readRef.current = controller;
      listTenantRoleHolders(tenant.id, controller.signal)
        .then((rows) => {
          setHolders(rows);
          focusOnOpen.current = focus;
          setPhase({ kind: "open" });
          // PEOPLE and not pairs, which is what the nav's word says ("N haltijaa") and what the list
          // below now shows. Counting the rows made the nav read "3 haltijaa" over one consultant who
          // held three codes — the same confusion the per-pair list was reported for.
          onHolders(groupHolders(rows).length);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted || err instanceof SessionExpiredError) return;
          // The 502 is its OWN state, and the section's message for it says the list is unknown rather
          // than empty. The nav is told null for the same reason: a count it cannot establish.
          const uncertain = err instanceof ApiError && err.status === 502;
          setPhase({ kind: "loadError", uncertain });
          onHolders(null);
        });
    },
    [tenant.id, onHolders],
  );

  /** Collapse back to the closed state and hand the keyboard back to the control that opened it. */
  const onCollapse = useCallback(() => {
    readRef.current?.abort();
    setError(null);
    setEmail("");
    setRoles([]);
    setQuery("");
    setPhase({ kind: "closed" });
    queueMicrotask(() => openRef.current?.focus());
  }, []);

  const onGrant = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (phase.kind !== "open") return;
      const address = email.trim();
      if (address === "") {
        setError(t("tenants.roles.emailRequired"));
        return;
      }

      // In the order the FORM offers the codes, not the order they were ticked. Which call goes first
      // decides which one may create the account and mail the activation link, and that must not depend
      // on the sequence of clicks that produced the same selection.
      const chosen = GRANTABLE_TENANT_ROLES.filter((code) => roles.includes(code));
      if (chosen.length === 0) {
        setError(t("tenants.roles.roleRequired"));
        return;
      }

      setError(null);
      // The SEARCH goes, because this submission is about to put somebody on the list and a filter left
      // over from looking for somebody else would hide them. The grant's own message would then name a
      // person the operator cannot find on the list it tells them to read.
      setQuery("");
      setPhase({ kind: "granting" });
      grantEachInTurn(tenant.id, address, chosen).then((outcome) => {
        if (outcome.kind === "signedOut") return;

        const codesIn = (state: RoleEnding["state"]) =>
          outcome.endings.filter((ending) => ending.state === state).map((ending) => ending.code);
        const grants = outcome.endings
          .filter((ending): ending is RoleEnding & { state: "granted" } => ending.state === "granted")
          .map((ending) => ending.grant);
        const unaudited = codesIn("applied");
        const notInEffect = codesIn("notInEffect");
        const undetermined = codesIn("undetermined");
        const ungranted = codesIn("notGranted");
        // IN EFFECT is granted AND applied, named rather than taken as "everything else": the response
        // said so for both, and that is what lets them stand in a sentence about somebody's real access.
        // An undetermined code is NOT in effect here — it is not known to be anything.
        const inEffect = outcome.endings
          .filter((ending) => ending.state === "granted" || ending.state === "applied")
          .map((ending) => ending.code);

        if (inEffect.length === 0 && undetermined.length === 0 && notInEffect.length === 0) {
          // Nothing was written, and every response said so: the ordinary refusal, and the form keeps
          // what was typed. No interpolation: the one message that took a value from the problem body was
          // §9's ceiling, whose `max` went with the ceiling (ADR-0021 §7).
          setError(t(grantErrorKey(outcome.refusal)));
          setPhase({ kind: "open" });
          return;
        }

        // Everything landed AND every customer-side row with it: the one outcome that is a plain success,
        // and the only one that reaches the page's success banner.
        if (
          unaudited.length === 0
          && notInEffect.length === 0
          && ungranted.length === 0
          && undetermined.length === 0
        ) {
          setEmail("");
          setRoles([]);
          onDone(inEffectMessage(t, tenant.slug, address, inEffect, grants));
          // Re-read rather than splicing the responses into the list: a grant may have re-asserted a
          // role somebody already had, and the server is the only thing that knows which carrier it
          // landed on. The read files one more audit row, which is honest — the operator did just look.
          read({ focus: false });
          return;
        }

        // Something is wrong with what this submission did. FOUR sentences, each omitted when it has
        // nothing to say, so no case needs a message of its own: what is in effect, which of those the
        // customer's log is missing, which code's outcome is not known, and what was not granted and why.
        //
        // The first is omitted too, and that is not symmetry for its own sake: a run whose only failure
        // was an undeclared 500 on the FIRST code has nothing in effect, and `inEffectMessage` would then
        // announce a grant out of an empty list of codes.
        const parts = inEffect.length > 0
          ? [inEffectMessage(t, tenant.slug, address, inEffect, grants)]
          : [];
        if (unaudited.length > 0) {
          parts.push(
            t("tenants.roles.auditIncomplete", {
              roles: unaudited.join(", "),
              count: unaudited.length,
            }),
          );
        }
        // The other half of the declared 500, and its own sentence because it says the opposite thing:
        // nobody's access changed, and submitting again is the remedy (ADR-0021 §8.1). It says nothing
        // about a row — see `RoleEnding`: one of its two causes leaves one and the other leaves none.
        if (notInEffect.length > 0) {
          parts.push(
            t("tenants.roles.notInEffect", {
              roles: notInEffect.join(", "),
              count: notInEffect.length,
            }),
          );
        }
        // The code the server broke on. It is named on its own and not folded into either neighbour: it is
        // the one code this screen cannot make a claim about, and the list it tells the operator to read is
        // re-read immediately below.
        if (undetermined.length > 0) {
          parts.push(t("tenants.roles.undetermined", { role: undetermined.join(", ") }));
        }
        if (ungranted.length > 0) {
          parts.push(
            t("tenants.roles.notGranted", {
              roles: ungranted.join(", "),
              reason: t(grantErrorKey(outcome.refusal)),
            }),
          );
        }

        // The ungranted, the NOT-IN-EFFECT and the undetermined codes are offered again — in the form's own
        // order, read back off the endings rather than concatenated, so the boxes do not come back in a
        // different order from the one they are listed in. An APPLIED one must not come back ticked: the
        // response that produced it forbids the retry, and a ticked box is this form's invitation to make
        // one. The other three are the opposite case — the grant writes are idempotent, so sending it again
        // is exactly the act that turns "not in effect" into access and "not known" into an answer, and
        // nothing else the operator can do here will. When nothing is left to retry the address goes too,
        // so the form is not half-armed.
        const offerAgain = outcome.endings
          .filter((ending) =>
            ending.state === "notGranted"
            || ending.state === "notInEffect"
            || ending.state === "undetermined")
          .map((ending) => ending.code);
        setRoles(offerAgain);
        if (offerAgain.length === 0) setEmail("");
        setError(parts.join(" "));
        // The list is re-read whatever this run established: a role landed (every applied one did), or the
        // list is the only thing that can answer for an undetermined code. The message above is about the
        // WRITE, so it has to survive that read.
        read({ focus: false, keepError: true });
      });
    },
    [phase.kind, email, roles, tenant.id, tenant.slug, onDone, read, t],
  );

  const onRevoke = useCallback(
    (pending: PendingRevoke) => {
      setPhase({ kind: "revoking", pending });
      revokeTenantRole(tenant.id, { subject: pending.subject, role: pending.role })
        .then((removed) => {
          // The banner's BOUND comes from the response and its NAME from the row — each from the only
          // place that knows it. The carrier is what the service removed (`removed.holder.carrier`, read
          // from the membership row or from the realm, §7), and the two bounds are not a nuance: a
          // membership removal has already taken effect, a client role lasts until the token is replaced,
          // and one sentence for both understated the first. The person is the list's label, which the
          // removal's own `holder` leaves null.
          onDone(
            t(revokedBannerKey(removed.holder.carrier), {
              person: pending.person,
              role: roleFull(t, pending.role),
              tenant: tenant.slug,
            }),
          );
          read({ focus: false });
        })
        .catch((err: unknown) => {
          if (err instanceof SessionExpiredError) return;
          setError(t(revokeErrorKey(err)));
          // Back to the list: the refusals are things the operator can react to (reload, pick another
          // row), and a dead alertdialog is not where that happens.
          setPhase({ kind: "open" });
        });
    },
    [tenant.id, tenant.slug, onDone, read, t],
  );

  const busy = phase.kind === "granting" || phase.kind === "revoking";
  const pending =
    phase.kind === "confirmRevoke" || phase.kind === "revoking" ? phase.pending : null;
  const confirmTitleId = "roles-confirm-title";
  const confirmBodyId = "roles-confirm-body";

  // One row per person, then the search box's narrowing — in that order, because the search is over the
  // PERSON and a filter applied to the wire rows would drop some of a kept person's chips.
  const people = useMemo(() => groupHolders(holders), [holders]);
  const shown = useMemo(() => filterHolders(people, query), [people, query]);

  return (
    <>
      <h2 id={headingId}>{t("tenants.roles.sectionTitle")}</h2>
      <p className="principal__intro">{t("tenants.roles.intro")}</p>

      {/* CLOSED — the default, and the state this section spends almost all its life in. The operator is
          told here that the people are not on screen yet and that asking for them is itself recorded. */}
      {phase.kind === "closed" && (
        <div className="principal__closed">
          <p className="principal__closed-note" role="note" data-testid="roles-audit-note">
            {t("tenants.roles.auditNote")}
          </p>
          <button
            type="button"
            className="btn"
            ref={openRef}
            onClick={() => read({ focus: true })}
            data-testid="tenant-roles-open"
          >
            {t("tenants.roles.rowAction")}
          </button>
        </div>
      )}

      {phase.kind === "loading" && (
        <p className="empty-state" role="status" aria-live="polite">
          {t("tenants.roles.loading")}
        </p>
      )}

      {phase.kind === "loadError" && (
        <>
          <p className="banner banner--error" role="alert" data-testid="roles-load-error">
            {t(phase.uncertain ? "tenants.roles.loadUncertain" : "tenants.roles.loadError")}
          </p>
          <div className="tenant-section__actions">
            <button type="button" className="btn" onClick={onCollapse} data-testid="roles-cancel">
              {t("tenants.roles.close")}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => read({ focus: true })}
              data-testid="roles-retry"
            >
              {t("tenants.roles.retry")}
            </button>
          </div>
        </>
      )}

      {phase.kind !== "closed" && phase.kind !== "loading" && phase.kind !== "loadError" && (
        <>
          <h3 className="principal__label">{t("tenants.roles.holdersTitle")}</h3>
          {people.length === 0 ? (
            <p className="empty-state" data-testid="roles-no-holders">
              {t("tenants.roles.noHolders")}
            </p>
          ) : (
            <>
              {/* THE SEARCH. A plain labelled field and not a `FilterScope` toolbar: there is one
                  criterion over one list, and the toolbar's badge, panel and reset exist to manage
                  several at once. `type="search"` so the browser offers its own clear control. */}
              <div className="roles__search">
                <label htmlFor="roles-filter">{t("tenants.roles.filterLabel")}</label>
                <input
                  id="roles-filter"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-describedby="roles-holders-count"
                  data-testid="roles-filter"
                />
              </div>

              {/* How many of them are on screen, and a LIVE REGION: narrowing a list by typing changes
                  it somewhere the typist is not looking, so the count is said rather than left to be
                  seen. It also names the scroller below, so the region's name says how much is in it. */}
              <p
                id="roles-holders-count"
                className="roles__count"
                role="status"
                aria-live="polite"
                data-testid="roles-holders-count"
              >
                {shown.length === people.length
                  ? t("tenants.roles.holderCount", { count: people.length })
                  : t("tenants.roles.holderCountFiltered", {
                      shown: shown.length,
                      total: people.length,
                    })}
              </p>

              {shown.length === 0 ? (
                <p className="empty-state" data-testid="roles-no-matches">
                  {t("tenants.roles.noMatches", { query: query.trim() })}
                </p>
              ) : (
                /* THE SCROLLER. A bounded height so the form below it stays reachable however many
                   people a customer has (M7: a consultant serves 50–100+ customers, and the busy
                   customers are the ones with several Incedo people named into them). Focusable with a
                   name, because a region a mouse can scroll has to be reachable by keyboard too
                   (WCAG 2.1.1) — the same arrangement as `TableScroll`, vertically. */
                <div
                  className="roles__scroll"
                  role="region"
                  aria-label={t("tenants.roles.holdersTitle")}
                  // A scrollable region must be keyboard-operable (axe "scrollable-region-focusable").
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                  tabIndex={0}
                  data-testid="roles-holders-scroll"
                >
                  <ul className="roles__holders" data-testid="roles-holders">
                    {shown.map((person) => (
                      <li className="roles__holder" key={person.subject} data-testid="roles-holder">
                        {/* WHO. The name when the list has one, and the address when it does not —
                            which is not a fallback but the ordinary membership case: a holder bound to
                            another tenant has no `users` row here, so their name is PII of a tenant
                            this read is not inside, and the address is what the operator typed. */}
                        <div className="roles__holder-id">
                          <span className="roles__holder-name" data-testid="roles-holder-person">
                            {person.name ?? person.email ?? person.subject}
                          </span>
                          {person.name && person.email && (
                            <span className="roles__holder-email" data-testid="roles-holder-email">
                              {person.email}
                            </span>
                          )}
                          {/* …AND WHERE THEY COME FROM, when that is somewhere else. "Who is this person"
                              has a different answer for Incedo's own consultant, for the customer's own
                              pääkäyttäjä and for somebody at a third party. The
                              CARRIER answered half of that question and ac90583a took it off the row for
                              being a sentence about Keycloak's internals; this is the useful half of the
                              same fact, as an organisation's name.

                              ONLY WHEN IT DIFFERS from the tenant on screen, which is what makes it worth
                              a line: a customer's own person has their home organisation in the page's own
                              heading already, and repeating it on every row would be noise on exactly the
                              list this is meant to make readable. The comparison is on the ID and not the
                              name — the name is editable by that tenant's own pääkäyttäjä, the id is not
                              (`RoleHolderPerson.homeTenantId`).

                              A home tenant whose NAME is unknown shows nothing. The server leaves it null
                              for a realm that would not answer, for an account bound to nothing, and for
                              an id this product has no tenant row for (ADR-0021 §8.2.1) — and a row
                              reading "Kotiorganisaatio: 5f1c…" would be worse than no line at all. */}
                          {showsHomeOrganisation(person, tenant.id) && (
                            <span className="roles__holder-home" data-testid="roles-holder-home">
                              {t("tenants.roles.holderHome", { organisation: person.homeTenantName })}
                            </span>
                          )}
                        </div>

                        {/* WHAT THEY HOLD — one chip per code, each with its own removal, so taking
                            one role off does not read as removing the person. A LIST, because that is
                            what it is: "two roles" is a fact a screen reader should be able to count
                            without walking the row. */}
                        <ul className="roles__chips" aria-label={t("tenants.roles.chipsLabel")}>
                          {person.roles.map((held) => (
                            <li className="roles__chip" key={held.role}>
                              <span
                                className={`role-pill roles__chip-label${chipTone(held.role)}`}
                                data-testid="roles-holder-role"
                                data-role-code={held.role}
                                data-carrier={held.carrier}
                              >
                                {roleName(t, held.role)}
                              </span>
                              <button
                                type="button"
                                className="roles__chip-revoke"
                                disabled={busy}
                                onClick={() =>
                                  setPhase({
                                    kind: "confirmRevoke",
                                    pending: {
                                      subject: person.subject,
                                      role: held.role,
                                      person: person.label,
                                      carrier: held.carrier,
                                    },
                                  })
                                }
                                data-testid="roles-revoke"
                                data-role-code={held.role}
                                aria-label={t("tenants.roles.revokeLabel", {
                                  person: person.label,
                                  role: roleName(t, held.role),
                                })}
                              >
                                {/* The glyph is decorative: the aria-label above is the button's
                                    name, and it names the person and the role. */}
                                <span aria-hidden="true">×</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          <form className="form principal__form" onSubmit={onGrant} aria-labelledby={headingId}>
            <label htmlFor="roles-email">{t("tenants.roles.emailLabel")}</label>
            <input
              id="roles-email"
              type="email"
              ref={emailRef}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-describedby="roles-email-hint"
              data-testid="roles-email"
            />
            <p id="roles-email-hint" className="tenants__slug-status">
              {t("tenants.roles.emailHint")}
            </p>

            {/* A fieldset and a legend rather than a label and a control: the question is one question
                ("which of these does this person hold here") answered by one independent box per code in
                `GRANTABLE_TENANT_ROLES`, and a <label> over a group is a name no screen reader reads with
                the boxes. */}
            <fieldset
              className="roles__choices"
              disabled={busy}
              aria-describedby="roles-role-hint"
            >
              <legend>{t("tenants.roles.roleLabel")}</legend>
              {GRANTABLE_TENANT_ROLES.map((code) => (
                <div className="roles__choice" key={code}>
                  <input
                    type="checkbox"
                    id={`roles-role-${code}`}
                    checked={roles.includes(code)}
                    onChange={(e) =>
                      setRoles((prev) =>
                        e.target.checked ? [...prev, code] : prev.filter((c) => c !== code),
                      )
                    }
                    data-testid="roles-role"
                    data-role-code={code}
                  />
                  <label htmlFor={`roles-role-${code}`}>{t(`tenants.roles.code.${code}`)}</label>
                </div>
              ))}
            </fieldset>
            <p id="roles-role-hint" className="tenants__slug-status">
              {t("tenants.roles.roleHint")}
            </p>

            {error && (
              <p className="field-error" role="alert" data-testid="roles-error">
                {error}
              </p>
            )}

            <div className="tenant-section__actions">
              <button
                type="button"
                className="btn"
                onClick={onCollapse}
                disabled={busy}
                data-testid="roles-cancel"
              >
                {t("tenants.roles.close")}
              </button>
              <button
                type="submit"
                className="btn btn--primary"
                disabled={busy}
                data-testid="roles-submit"
              >
                {phase.kind === "granting" ? t("tenants.roles.granting") : t("tenants.roles.grant")}
              </button>
            </div>
          </form>
        </>
      )}

      {/* The decision point. A modal, like the handover's: it takes an authority away from a named
          person, and a confirmation that could be scrolled past is not a decision point. The body says
          what the removal does NOT do, because that is the half an operator would otherwise guess at. */}
      {pending && (
        <Modal
          role="alertdialog"
          className="principal"
          labelledBy={confirmTitleId}
          describedBy={confirmBodyId}
          testId="roles-confirm"
          onClose={() => setPhase({ kind: "open" })}
          closeDisabled={busy}
        >
          <h2 id={confirmTitleId}>{t("tenants.roles.confirmTitle")}</h2>
          <p id={confirmBodyId} data-testid="roles-confirm-body">
            {/* The bound this names is the CARRIER's, not the surface's: a membership row is gone as soon
                as the removal answers, a client role lives until the token is replaced. */}
            {t(revokeConfirmKey(pending.carrier), {
              person: pending.person,
              role: roleFull(t, pending.role),
            })}
          </p>
          <p className="principal__tenant">
            {t("tenants.roles.confirmTenant", { tenant: tenant.slug })}
          </p>
          <p className="principal__note">{t("tenants.roles.confirmKeeps")}</p>
          <div className="modal__actions">
            <button
              type="button"
              className="btn"
              onClick={() => setPhase({ kind: "open" })}
              disabled={busy}
              data-testid="roles-confirm-cancel"
            >
              {t("tenants.roles.cancel")}
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => onRevoke(pending)}
              disabled={busy}
              data-testid="roles-confirm-submit"
            >
              {busy ? t("tenants.roles.revoking") : t("tenants.roles.revoke")}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

/** What one `POST …/roles` did to the code it named. The form reports and re-offers by this, not by status. */
type RoleEnding =
  /** 200 — written, audited in both chains. */
  | { code: string; state: "granted"; grant: TenantRoleGrant }
  /**
   * The route's DECLARED 500 with `change_in_effect`, recognised by that response and not by the status.
   * The role IS in effect and the customer's audit row is not: a retry is refused by the response itself,
   * so this code is named as granted, reported as unaudited, and NOT ticked again.
   */
  | { code: string; state: "applied" }
  /**
   * The same declared 500 with `change_not_in_effect` (ADR-0021 §8.1). NOBODY'S ACCESS CHANGED — the
   * opposite conclusion from the same status, which is why the `error` code and not the title decides it.
   * Named as not in effect, and offered again, because the retry is the remedy.
   *
   * WHAT IT LEFT BEHIND IS NOT ONE THING, and this console must not claim it is. The service reaches this
   * code from either of two failures: the customer's chain, which leaves the membership row written and
   * inert, or the operator's chain, which is inside the transaction that writes the row and so leaves no
   * row at all. Both mean the same thing to the operator and only that much is said — not in effect,
   * nobody's access changed, send it again — because any sentence about a row is false on one of them.
   */
  | { code: string; state: "notInEffect" }
  /**
   * A 500 the route does not declare — the pipeline's generic answer to an unhandled exception. It
   * establishes NOTHING about the write, and both readings of it would be a claim this console cannot
   * make: the grant may have landed (the realm write comes before the audit append that can throw) or the
   * request may have died before the realm was touched at all (the customer is read from the database
   * first). So the code is named as unknown, and the holder list read below is what settles it.
   */
  | { code: string; state: "undetermined" }
  /** Refused, or never attempted because an earlier code stopped the run. Nothing was written for it. */
  | { code: string; state: "notGranted" };

/** What a submission did: where every ticked code ended up, and the refusal that stopped it if one did. */
type GrantOutcome =
  /** The run finished. `refusal` is the error that stopped it, or null if nothing refused. */
  | { kind: "ran"; endings: readonly RoleEnding[]; refusal: unknown }
  /** The session went away mid-run. The app is already navigating to sign-in; this screen says nothing. */
  | { kind: "signedOut" };

/**
 * One `POST …/roles` per ticked code, ONE AT A TIME, stopping at the first REFUSAL.
 *
 * Awaited one after the other and not in parallel, which is the whole reason this is a function and not
 * a `Promise.all`: the route creates the realm account for an address it has never seen and mails the
 * activation link from that same call, so two calls for one unknown address would race over creating it.
 * In turn, the first call creates the account and the second finds it and writes only the role.
 *
 * It stops at the first refusal rather than trying the rest. The refusals this route gives are facts
 * about the ADDRESS and not about the code — §9's ceiling, a 502 from the identity provider, a 403 — so
 * a second call would almost always be refused the same way, and an account the first call could not
 * create is not there for the second one either. The caller names what was not attempted as not granted,
 * which is what it is.
 *
 * THE DECLARED 500 IS NOT ONE OF THOSE, AND THE RUN CARRIES ON PAST IT. `AuditTrailIncomplete` says one of
 * the two audit chains is missing its row, so none of the reasoning above applies: the account the next
 * call needs exists, because this call either found it or made it. Stopping would leave the second code
 * ungranted over an obstacle that has nothing to do with it, and the only recovery open to the operator —
 * tick it and submit again — is the very call being skipped. So it is recorded as `applied` or as
 * `notInEffect`, whichever its `error` code says, and the next code is attempted.
 *
 * AN UNDECLARED 500 STOPS IT, and that is the difference the distinction buys. The run carries on past the
 * declared one because the response says what the next call can rely on; an undeclared one says nothing, so
 * the thing that made the account exist may not have happened — and the usual cause, a server that is
 * failing, would answer the next call the same way. The code it happened on is reported as unknown rather
 * than as refused.
 */
async function grantEachInTurn(
  tenantId: string,
  email: string,
  roles: readonly string[],
): Promise<GrantOutcome> {
  const endings: RoleEnding[] = [];
  for (const [index, role] of roles.entries()) {
    try {
      endings.push({ code: role, state: "granted", grant: await grantTenantRole(tenantId, { email, role }) });
    } catch (error: unknown) {
      if (error instanceof SessionExpiredError) return { kind: "signedOut" };
      if (isAuditTrailIncomplete(error)) {
        // The run carries on past EITHER half of the declared 500 and for one reason: the account the next
        // call needs exists, because this call either found it or made it. Which half it was decides what
        // this code is reported as, not whether the next one is attempted.
        endings.push({ code: role, state: isChangeNotInEffect(error) ? "notInEffect" : "applied" });
        continue;
      }
      // The run stops here, and the code this call named ends UNDETERMINED when the response does not
      // establish what happened to it and `notGranted` when it does. The codes AFTER it were never
      // attempted, which is not an uncertainty: nothing was sent for them.
      const own: RoleEnding = isUndeterminedWrite(error)
        ? { code: role, state: "undetermined" }
        : { code: role, state: "notGranted" };
      return {
        kind: "ran",
        endings: [
          ...endings,
          own,
          ...roles.slice(index + 1).map((code) => ({ code, state: "notGranted" }) as const),
        ],
        refusal: error,
      };
    }
  }
  return { kind: "ran", endings, refusal: null };
}

/**
 * The `title` of the route's declared audit-incomplete 500, verbatim from `PlatformTenantEndpoints`.
 *
 * THE RESPONSE AND NOT THE STATUS, which is the whole point of this constant. `AuditTrailIncomplete` is the
 * only 500 that mapping declares, but it is not the only 500 the route can answer: outside Development the
 * pipeline answers any unhandled exception with a generic ProblemDetails 500 (`Program.cs`), and this route
 * has such a place on either side of the realm write — the database read that finds the customer, before
 * it, and the operator-side audit append, after it. Reading the status alone therefore claims a role is in
 * effect on a response that may mean nothing was written at all.
 *
 * The title is what distinguishes it from an undeclared 500; the `error` extension then says which of its
 * two endings it is ({@link isChangeNotInEffect}). Neither coupling is left to a reader's goodwill:
 * `platform-tenant-roles.realstack.spec.ts` drives both endings through the real stack and asserts their
 * wording, so a backend that changed the title or dropped the code would fail there rather than silently
 * turn this console's reading of it back into a guess.
 */
const AUDIT_INCOMPLETE_TITLE = "The role change is only partly audited";

/**
 * Is this the route's DECLARED audit-incomplete 500 at all? It is what decides that the run may carry on
 * past this code, so it must be the declared response and nothing wider.
 */
function isAuditTrailIncomplete(err: unknown): boolean {
  return (
    err instanceof ApiError && err.status === 500 && err.problem?.title === AUDIT_INCOMPLETE_TITLE
  );
}

/**
 * …and is it the half where NOTHING is in effect (ADR-0021 §8.1)? The membership is activated only once
 * both audit rows are in, so a chain that could not be written leaves the person reaching nothing and the
 * retry is the remedy. WHICH chain failed is not asked and must not be guessed from this: the customer's
 * leaves the row inert, the operator's leaves no row, and the one `error` code covers both.
 *
 * Read off the `error` code, because the two halves share a status and a title and ask opposite things of
 * the operator. A response whose code is missing or unrecognised is treated as the OTHER half — the
 * surface's older and more dangerous one — so an unexpected body makes this console withhold the retry
 * rather than offer one against a role that may really be in effect.
 */
function isChangeNotInEffect(err: unknown): boolean {
  return err instanceof ApiError && err.problem?.error === CHANGE_NOT_IN_EFFECT;
}

/**
 * Did the response leave this code's outcome UNKNOWN? A 500 that is not the declared one: the server broke
 * somewhere it does not describe, and the role may or may not have been written.
 *
 * The route's other failures are not this. Each one of them is a DECLARED answer whose own detail says
 * what happened to the write — the 502 says the identity provider did not act, the 422s refuse before the
 * realm is touched — so those are reported as not granted, which is what they say they are. A transport
 * that never produced a response is not this either: `grantErrorKey` has always read it as nothing granted,
 * and widening that is a separate question from the one the declared-500 confusion raised.
 */
function isUndeterminedWrite(err: unknown): boolean {
  return err instanceof ApiError && err.status === 500 && !isAuditTrailIncomplete(err);
}

/**
 * The sentence naming what this person now holds. A grant that CREATED the realm account says so, because
 * the person cannot use the role until they follow Keycloak's activation link — and a grant whose mail did
 * not leave says THAT, because then nobody has sent them anything at all.
 *
 * Over a multi-role submission the three messages are chosen from the WHOLE run: the account is created by
 * at most one of the calls (the first one that does not find it), so that call's outcome is the one with
 * something to say — the other call found the account the first one had just made, and a message taken
 * from it would tell the operator no activation link was needed when one was.
 *
 * What that second message must NOT say is "resend it from the provisioning section". The step-2 resend
 * addresses the tenant's `first_admin_subject`, which a role grant never sets, so it would refuse or write
 * to a different person — and repeating the grant mails nothing either, because the activation mail goes
 * out only on the call that creates the account. The recovery this account really supports is the ordinary
 * one: it is enabled and carries the address, so the person asks the customer's own sign-in page for a new
 * password link (`resetPasswordAllowed` is on in both realms). The message says that instead, and says the
 * grant itself is in effect either way.
 *
 * `codes` is what is IN EFFECT and not what answered, so an applied code is named here too — and `grants`
 * is only the calls that answered, which is what the account-and-mail half can be read from. When no call
 * answered at all (every one of them a 500) the sentence falls back to the ADDRESS the operator typed,
 * because the realm's display name only ever arrives in a response body. The cost of that case is stated
 * rather than guessed at: nothing then says whether the address got an account, since the response that
 * would have carried it is the one that failed. The operator is told to report the error, which is the act
 * that recovers it; inventing a sentence about an account this screen cannot see would not.
 */
function inEffectMessage(
  t: (key: string, options?: Record<string, unknown>) => string,
  slug: string,
  address: string,
  codes: readonly string[],
  grants: readonly TenantRoleGrant[],
): string {
  const values = {
    person: grants.length > 0 ? holderLabel(grants[0].holder) : address,
    roles: codes.join(", "),
    // i18next picks `_one` / `_other` from this, so "sai roolin" and "sai roolit" are two strings a
    // translator can inflect rather than one sentence that is wrong at one of the two counts.
    count: codes.length,
    tenant: slug,
  };
  const created = grants.find((grant) => grant.account === ACCOUNT_CREATED);
  // The ordinary grant, and its bound is the CARRIER's and not this surface's: a membership is live at the
  // grantee's next request, a client role at their next token ({@link grantedBannerKey}). The carrier is on
  // each answered grant's holder, which is the same row the list renders it from.
  if (!created) return t(grantedBannerKey(grants.map((grant) => grant.holder.carrier)), values);
  return created.mail === MAIL_SEND_FAILED
    ? t("tenants.roles.doneCreatedNoMail", values)
    : t("tenants.roles.doneCreated", values);
}

/**
 * The message a refused grant gets. The 422 is one message again: the surface had two, told apart by the
 * problem's `error` code because the remedies were opposite, until ADR-0021 §7 removed §9's twenty-tenant
 * ceiling and the "that account is full" half with it.
 *
 * THE TWO 500s GET DIFFERENT MESSAGES, for the reason {@link isAuditTrailIncomplete} exists. The declared
 * one is deliberately NOT "try again" — there the grant has already taken effect and only the
 * customer-side audit row is missing — and saying that of an undeclared 500 would tell the operator a role
 * is in effect when the request may have died before anything was written. `errorServer` says what such a
 * response actually establishes, which is the failure and not the fate of the write.
 */
function grantErrorKey(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.status) {
      case 403:
        return "tenants.roles.error403";
      case 404:
        return "tenants.roles.error404";
      case 409:
        return "tenants.roles.error409";
      case 422:
        return "tenants.roles.error422";
      case 500:
        if (!isAuditTrailIncomplete(err)) return "tenants.roles.errorServer";
        // The declared one, and its two halves say opposite things about the role. Reachable here only
        // through the `reason` of a run that was stopped by something else, because the run itself carries
        // on past this ending — but a message that said "in effect" of a grant that is not would be the one
        // mistake this distinction exists to prevent, so it is told apart here too.
        return isChangeNotInEffect(err)
          ? "tenants.roles.error500NotInEffect"
          : "tenants.roles.error500";
      case 502:
        return "tenants.roles.error502";
      default:
        return "tenants.roles.errorNetwork";
    }
  }
  // Never reached the server (offline, aborted transport): nothing was granted.
  return "tenants.roles.errorNetwork";
}

/**
 * The same, for a removal. Its 404 is the one that differs: the role is already off this tenant.
 *
 * Its 500 has the same two halves as the grant's since ADR-0021 §8.1, and for the same reason — a
 * membership removal and the operator's audit row are one transaction, so a platform chain that could not
 * be written means the role is STILL HELD and the retry is the remedy. The other half is the one this
 * surface has always had: the removal is in effect and only the customer's row is missing.
 *
 * ITS 409 IS ADR-0026 §4's FLOOR, and it is a refusal the grant never answers: this would have been the
 * tenant's last `ADMIN`, so it was not removed. It needs a message of its own and not the generic one,
 * because it is the only refusal on this surface whose remedy is ANOTHER ACT ON THIS SAME FORM — grant
 * `ADMIN` to somebody else, then come back — rather than a reload, a correction in the Keycloak console, or
 * a retry of the same request. Until this task it had no arm at all and fell to `errorNetwork`, which told
 * the operator a protection working exactly as the owner asked for was a network error.
 *
 * THE CODE AND NOT THE STATUS DECIDES IT, and the fallback is a 409 of its own rather than the network
 * message. The route's two surfaces share one status map (`PlatformTenantEndpoints`), where 409 is also the
 * GRANT's malformed-`tenant_id` refusal — a removal cannot reach that one today (it has no account to
 * choose a carrier for: an unreadable binding leaves the role held by neither carrier here, which is the
 * 404 above) — so what `revokeError409` really covers is a 409 this build does not know. It says the one
 * thing true of every 409 on this surface: the state refused a well-formed request and nothing was removed.
 * Which is why the `ADMIN` sentence may not be the fallback: a build whose server grew a second 409 would
 * otherwise tell the operator to grant somebody else `ADMIN` over a refusal that has nothing to do with it.
 */
function revokeErrorKey(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.status) {
      case 403:
        return "tenants.roles.error403";
      case 404:
        return "tenants.roles.revokeError404";
      case 409:
        return err.problem?.error === LAST_ADMINISTRATOR
          ? "tenants.roles.revokeError409LastAdmin"
          : "tenants.roles.revokeError409";
      case 422:
        return "tenants.roles.error422";
      case 500:
        return isChangeNotInEffect(err)
          ? "tenants.roles.revokeError500NotInEffect"
          : "tenants.roles.revokeError500";
      // ADR-0018 §10's unreachable identity provider AND ADR-0026 §4c's indeterminate count, which the
      // backend answers with the same status and no code. The endings do NOT agree about what happened: §4c
      // and the pre-write reads refuse before anything is written, but the realm write's own failure covers
      // a role-mapping DELETE that was SENT and whose answer never came back
      // (`KeycloakAdminClient.WriteRoleMappingAsync`, which is why the server's detail for it says the role
      // "may still be held"). So the one message this status gets keeps the outcome OPEN and asks for the
      // retry, which is safe because the write is idempotent; what it must not say is either "the removal is
      // in effect" (the 500's sentence) or "nothing was removed" (the 409s', which know it).
      case 502:
        return "tenants.roles.revokeError502";
      default:
        return "tenants.roles.errorNetwork";
    }
  }
  return "tenants.roles.errorNetwork";
}
